import { useFetch } from "~vue/api";
import type { PerspectiveTopicCategory } from "~vue/pages/perspectiveTypes";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref } from "vue";

interface StartPerspectiveTopicChatResponse {
    coaching_session_url: string;
}

interface ChatPayload {
    letters: string;
    topic_category: PerspectiveTopicCategory;
    topic_index: number;
}

/**
 * Starts a perspective-topic "Dive Deeper" chat for the strength / growth-area
 * cards on the user-profile Archetype tab. POSTs to the coach_api endpoint at
 * /v1/perspectives/topic-chat with (letters, category, index); the server
 * resolves the topic title / body from the canonical PERSPECTIVE_TYPES table
 * so the client cannot inject arbitrary text into the prompt.
 */
export function useStartPerspectiveTopicChat(category: PerspectiveTopicCategory) {
    const chatPayload = ref<ChatPayload | null>(null);
    const chatError = ref<string | null>(null);

    const {
        data: chatData,
        isFetching: isStartingChat,
        error: chatFetchError,
        execute: startChat,
    } = useFetch<ChatPayload>("/v1/perspectives/topic-chat", { immediate: false }).post(chatPayload).json<StartPerspectiveTopicChatResponse>();

    async function handleStartChat(letters: string, index: number) {
        if (isStartingChat.value) {
            return;
        }
        void logUserInteraction(AmplitudeEventNames.PERSPECTIVE_TOPIC_CLICKED, { topic_category: category, topic_index: index, letters });
        chatError.value = null;
        chatPayload.value = { letters, topic_category: category, topic_index: index };
        await startChat();
        if (chatFetchError.value || !chatData.value?.coaching_session_url) {
            chatError.value = "Something went wrong. Please try again.";
        } else {
            openUrl(chatData.value.coaching_session_url);
        }
    }

    return {
        isStartingChat,
        chatError,
        handleStartChat,
    };
}
